import { CommandGetCollection } from "./commands/cmdCommandGetCollection";
import { CommandGetCollections } from "./commands/cmdCommandGetCollections";
import { CommandGetContentCatalogFolders } from "./commands/cmdCommandGetContentCatalogFolders";
import { CommandCreateAd, type CreateAdPayload } from "./commands/cmdCreateAd";
import {
  CommandCreateFolder,
  type CreateFolderPayload,
} from "./commands/cmdCreateFolder";
import { CommandDeleteAd } from "./commands/cmdDeleteAd";
import { CommandDeleteFolder } from "./commands/cmdDeleteFolder";
import { CommandGetAdProvenance } from "./commands/cmdGetAdProvenance";
import { CommandGetAdTokenNotes } from "./commands/cmdGetAdTokenNotes";
import { CommandRetitleAd } from "./commands/cmdRetitleAd";
import { CommandDuplicateAd } from "./commands/cmdDuplicateAd";
import { CommandDuplicateTemplate } from "./commands/cmdDuplicateTemplate";
import { CommandForkAd } from "./commands/cmdForkAd";
import { CommandGetAd } from "./commands/cmdGetAd";
import { CommandOpenMailPieceSide } from "./commands/cmdOpenMailPieceSide";
import { CommandGetTagCollection } from "./commands/cmdGetTagCollection";
import { CommandLinkAds } from "./commands/cmdLinkAds";
import { CommandListAdTokens } from "./commands/cmdListAdTokens";
import { CommandListAds as CommandListAds } from "./commands/cmdListAds";
import { CommandMoveFolder, type MoveFolderPayload } from "./commands/cmdMoveFolder";
import { CommandReorderFolder } from "./commands/cmdReorderFolder";
import { CommandSearchContent } from "./commands/cmdSearchContent";
import { CommandUnlinkAds } from "./commands/cmdUnlinkAds";
import { CommandUpdateAd, type UpdateAdPayload } from "./commands/cmdUpdateAd";
import {
  CommandUpdateFolder,
  type UpdateFolderPayload,
} from "./commands/cmdUpdateFolder";

export class ContentCatalogService {
  async getCollections(organizationId: number) {
    const cmd = new CommandGetCollections();
    const results = await cmd.run(organizationId);
    return results;
  }

  async getFolders(
    organizationId: number,
    parentId?: number,
    withAds?: boolean,
    type?: "collection" | "folder",
    signal?: AbortSignal
  ) {
    const cmd = new CommandGetContentCatalogFolders();
    const results = await cmd.run(organizationId, parentId, withAds, type, signal);
    return results;
  }

  async getCollection(id: string) {
    const cmd = new CommandGetCollection();
    const results = await cmd.run(id);
    return results;
  }

  async getTagCollection(organizationId: string, tagId: string) {
    const cmd = new CommandGetTagCollection();
    const results = await cmd.run(organizationId, tagId);
    return results;
  }

  async createFolder(organizationId: number, payload: CreateFolderPayload) {
    const cmd = new CommandCreateFolder();
    const results = await cmd.run(organizationId, payload);
    return results;
  }

  async moveFolder(payload: MoveFolderPayload) {
    const cmd = new CommandMoveFolder();
    const results = await cmd.run(payload);
    return results;
  }

  async reorderFolder(parentFolderId: number, orderedIds: number[]) {
    const cmd = new CommandReorderFolder();
    const results = await cmd.run(parentFolderId, orderedIds);
    return results;
  }

  async updateFolder(
    organizationId: number,
    folderId: number,
    payload: UpdateFolderPayload,
  ) {
    const cmd = new CommandUpdateFolder();
    const results = await cmd.run(organizationId, folderId, payload);
    return results;
  }

  async listAds(folderId: number) {
    const cmd = new CommandListAds();
    const results = await cmd.run(folderId);
    return results;
  }

  async deleteAd(adId: number) {
    const cmd = new CommandDeleteAd();
    const results = await cmd.run(adId);
    return results;
  }

  async deleteFolder(organizationId: number, folderId: number) {
    const cmd = new CommandDeleteFolder();
    const results = await cmd.run(organizationId, folderId);
    return results;
  }

  async retitleAd(adId: number, name: string) {
    return await new CommandRetitleAd().run(adId, name);
  }

  async getAdProvenance(adIds: number[]) {
    return await new CommandGetAdProvenance().run(adIds);
  }

  async getAdTokenNotes(adIds: number[]) {
    return await new CommandGetAdTokenNotes().run(adIds);
  }

  async duplicateAd(adId: number, payload?: { name?: string; is_template?: boolean; is_default?: boolean }) {
    const cmd = new CommandDuplicateAd();
    const results = await cmd.run(adId, payload);
    return results;
  }

  /** Copy a locked ad's tree so it can be edited. See CommandForkAd for adGroupId. */
  async forkAd(adId: number, adGroupId?: number) {
    const cmd = new CommandForkAd();
    const results = await cmd.run(adId, adGroupId);
    return results;
  }

  async duplicateTemplate(templateId: number, folderId: number) {
    const cmd = new CommandDuplicateTemplate();
    const results = await cmd.run(templateId, folderId);
    return results;
  }

  async searchContent(filter: object) {
    const cmd = new CommandSearchContent();
    const results = await cmd.run(filter);
    return results;
  }

  async getAd(adId: number, expand?: Array<"content" | "children">) {
    const cmd = new CommandGetAd();
    const results = await cmd.run(adId, expand);
    return results;
  }

  async createAd(payload: CreateAdPayload) {
    const cmd = new CommandCreateAd();
    const results = await cmd.run(payload);
    return results;
  }

  /** The Kinema composition for one side of a postcard, created if the side has none yet. */
  async openMailPieceSide(adId: number, side: "front" | "back") {
    return await new CommandOpenMailPieceSide().run(adId, side);
  }

  /** The ad token vocabulary. Read once per page load and filtered in memory -- see useAdTokens. */
  async listAdTokens() {
    const cmd = new CommandListAdTokens();
    return await cmd.run();
  }

  async updateAd(adId: number, payload: UpdateAdPayload) {
    const cmd = new CommandUpdateAd();
    const results = await cmd.run(adId, payload);
    return results;
  }

  async linkAds(organizationId: number, folderId: number, adIds: number[]) {
    const cmd = new CommandLinkAds();
    const results = await Promise.all(
      adIds.map((adId) => cmd.run(organizationId, folderId, adId)),
    );
    return results;
  }

  async unlinkAds(organizationId: number, folderId: number, adIds: number[]) {
    const cmd = new CommandUnlinkAds();
    const results = await Promise.all(
      adIds.map((adId) => cmd.run(organizationId, folderId, adId)),
    );
    return results;
  }
}
